/**
 * product.js
 *
 * Handles the PDP purchase flow:
 *
 * 1. PURCHASE OPTION TOGGLE — switch between Subscribe & Save and One-time
 *    Purchase. Updates card styling, the hidden selling_plan input, and the
 *    button text/price from pre-rendered data attributes (no network call).
 *
 * 2. FORM SUBMIT — adds the variant (qty 1, ± selling_plan) via the global
 *    cart drawer (WillowCart.add). The drawer opens, shows a debounced
 *    spinner during the request, and re-renders from the Section Rendering
 *    API. Falls back to /cart on the rare case the drawer JS hasn't loaded.
 */

(function () {
  'use strict';

  const form = document.querySelector('.js-product-form');
  if (!form) return;

  // Read a bundle's component list (rendered server-side by
  // snippets/bundle-components-payload.liquid as a JSON script tag) and
  // return a compact JSON string suitable for the `_bundle_components`
  // line-item property. The fields match what the pixel synthesises in the
  // webhook component rows: productID, name, category, price, sku,
  // quantityPerBundle.
  function readBundleComponentsJson(bundleId) {
    if (!bundleId) return '';
    const scriptEl = document.querySelector(
      'script[data-bundle-components-register][data-bundle-id="' + bundleId + '"]'
    );
    if (!scriptEl) return '';
    try {
      const data = JSON.parse(scriptEl.textContent || '{}');
      const comps = data && data.bundles && data.bundles[bundleId];
      return Array.isArray(comps) && comps.length ? JSON.stringify(comps) : '';
    } catch (e) {
      return '';
    }
  }

  // ── DOM refs ────────────────────────────────────────────────────────────
  const submitBtn        = form.querySelector('.js-product-submit');
  const submitText       = form.querySelector('.js-submit-text');
  const submitPrice      = form.querySelector('.js-submit-price');
  const sellingPlanInput = form.querySelector('.js-selling-plan-input');
  const errorEl          = form.querySelector('.js-product-error');
  const purchaseOptions  = form.querySelectorAll('.js-purchase-option');
  // Skincare-only: the headline price above the benefits. It lives OUTSIDE the
  // form, so query the document. Null on every other layout.
  const skincarePrice    = document.querySelector('.js-skincare-price');

  // ── Pre-rendered prices + labels from Liquid data attributes ────────────
  const subPrice            = form.dataset.subPrice;
  const otpPrice            = form.dataset.otpPrice;
  const btnSubscribe        = form.dataset.btnSubscribe;
  const btnOtp              = form.dataset.btnOtp;
  const btnSubscribeLoading = form.dataset.btnSubscribeLoading;
  const btnOtpLoading       = form.dataset.btnOtpLoading;

  function readBundleComponentsJson(bundleId) {
    if (!bundleId) return null;
    const el = document.querySelector('[data-bundle-components-register][data-bundle-id="' + bundleId + '"]');
    if (!el) return null;

    try {
      const parsed = JSON.parse(el.textContent || '{}');
      const bundles = parsed && parsed.bundles;
      const components = bundles && bundles[String(bundleId)];
      return Array.isArray(components) && components.length ? JSON.stringify(components) : null;
    } catch (error) {
      console.warn('[product.js] failed to read bundle components', error);
      return null;
    }
  }

  function publishCartSnapshot(cart) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('cart_snapshot', { cart: cart });
      }
    } catch (error) {
      console.warn('[product.js] failed to publish cart snapshot', error);
    }
  }

  // ── State ───────────────────────────────────────────────────────────────
  // Read the last selection from sessionStorage so navigating back from
  // checkout (whether via re-fetch or bfcache) restores the user's choice.
  var defaultMode  = form.dataset.noSubscription ? 'otp' : 'subscribe';
  var storedMode   = sessionStorage.getItem('willow_purchase_mode');
  let currentMode  = (storedMode === 'subscribe' || storedMode === 'otp') ? storedMode : defaultMode;

  // ── Purchase option UI update ────────────────────────────────────────────
  function setMode(mode) {
    currentMode = mode;
    sessionStorage.setItem('willow_purchase_mode', mode);

    purchaseOptions.forEach(function (option) {
      var active = option.dataset.mode === mode;
      option.classList.toggle('purchase-option--active', active);
      // Sync radio.checked so :has(input:checked) CSS always matches the active
      // option — critical when the page is restored from bfcache and the browser
      // resets radio states to their HTML-source values.
      var radio = option.querySelector('input[type="radio"]');
      if (radio) radio.checked = active;
    });

    if (sellingPlanInput) {
      sellingPlanInput.disabled = mode !== 'subscribe';
    }

    if (mode === 'subscribe') {
      if (submitText)  submitText.textContent  = btnSubscribe;
      if (submitPrice) submitPrice.textContent = subPrice;
      if (submitBtn)   submitBtn.setAttribute('aria-label', btnSubscribe);
      if (skincarePrice) skincarePrice.textContent = subPrice;
    } else {
      if (submitText)  submitText.textContent  = btnOtp;
      if (submitPrice) submitPrice.textContent = otpPrice;
      if (submitBtn)   submitBtn.setAttribute('aria-label', btnOtp);
      if (skincarePrice) skincarePrice.textContent = otpPrice;
    }
  }

  // Apply initial state
  setMode(currentMode);

  // ── Max-qty guard ────────────────────────────────────────────────────────
  // Mirror the cart drawer's per-line limit on the PDP: once a line for the
  // selected variant+selling_plan reaches the max, disable Add to cart so
  // customers can't bypass the drawer's clamp by repeatedly hitting ATC here.
  function getMaxQty() {
    var drawer = document.querySelector('cart-drawer');
    var raw = drawer && drawer.dataset.maxQty;
    var n = Number(raw);
    return n > 0 ? n : 4;
  }

  function currentSellingPlanId() {
    if (currentMode === 'subscribe' && sellingPlanInput && !sellingPlanInput.disabled) {
      return Number(sellingPlanInput.value) || null;
    }
    return null;
  }

  async function lineQtyInCart() {
    try {
      var res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return 0;
      var cart = await res.json();
      var variantId = Number(form.querySelector('[name="id"]').value);
      var planId = currentSellingPlanId();
      return (cart.items || []).reduce(function (sum, item) {
        if (item.variant_id !== variantId) return sum;
        var itemPlan = item.selling_plan_allocation
          ? Number(item.selling_plan_allocation.selling_plan.id)
          : null;
        return itemPlan === planId ? sum + item.quantity : sum;
      }, 0);
    } catch (e) {
      return 0;
    }
  }

  async function refreshAtcAvailability() {
    if (!submitBtn) return;
    var inCart = await lineQtyInCart();
    var max = getMaxQty();
    var atMax = inCart >= max;
    submitBtn.disabled = atMax;
    submitBtn.classList.toggle('product-form__submit--max', atMax);
    if (atMax) {
      var maxText = submitBtn.dataset.maxText || 'Max in cart';
      if (submitText) submitText.textContent = maxText;
      submitBtn.setAttribute('aria-label', maxText);
    } else {
      // Restore the normal label/price for the active mode.
      setMode(currentMode);
    }
  }

  // Re-check after the cart drawer mutates anything (qty change, remove,
  // OTP↔S&S toggle, promo code).
  document.addEventListener('cart:updated', refreshAtcAvailability);

  refreshAtcAvailability();

  // Re-apply when the page is restored from bfcache (persisted pageshow).
  // bfcache freezes the DOM mid-submit: the button is still disabled and its
  // text still shows the loading copy. Re-enable it and restore the correct
  // mode so the page is immediately interactive again.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      if (submitBtn) submitBtn.disabled = false;
      var saved = sessionStorage.getItem('willow_purchase_mode');
      setMode((saved === 'subscribe' || saved === 'otp') ? saved : defaultMode);
      if (typeof refreshAtcAvailability === 'function') refreshAtcAvailability();
    }
  });

  // ── Listen for radio changes ─────────────────────────────────────────────
  // Using 'change' on the form covers:
  //   - Mouse click on a card label
  //   - Arrow-key navigation between radios (keyboard users)
  //   - Any programmatic radio.checked update that triggers a change event
  form.addEventListener('change', function (event) {
    if (event.target.name === 'purchase_mode') {
      setMode(event.target.value);
      refreshAtcAvailability();
    }
  });

  // ── Form submit ──────────────────────────────────────────────────────────
  // Add the variant to the cart and hand off to the global cart drawer.
  // WillowCart.add() opens the drawer, shows the debounced spinner, and
  // re-renders the drawer via the Section Rendering API. No clear, no
  // checkout redirect — purchases now flow through the drawer.
  form.addEventListener('submit', async function (event) {
    event.preventDefault();
    clearError();

    if (submitBtn) submitBtn.disabled = true;
    if (submitText) submitText.textContent = currentMode === 'subscribe' ? btnSubscribeLoading : btnOtpLoading;

    const variantId = Number(form.querySelector('[name="id"]').value);
    const payload   = { id: variantId, quantity: 1 };

    if (currentMode === 'subscribe' && sellingPlanInput && !sellingPlanInput.disabled) {
      payload.selling_plan = Number(sellingPlanInput.value);
    }

    // Tag bundle lines with `_item_type` so the analytics pixel can detect
    // them from the cart snapshot. Shopify hides `_`-prefixed properties
    // from the checkout UI and from the cart drawer's properties display.
    //
    // We ALSO embed the component list as JSON in `_bundle_components`.
    // The Custom Pixel sandbox at /checkout is a fresh instance that never
    // saw the storefront's `bundle_components_register` event, so the only
    // way to expand the bundle into its parts at CheckoutStarted /
    // CheckoutComplete is to carry the components inline on the line item
    // (line-item properties survive into checkout). Property values can be
    // up to ~50K chars — a 2-3 component bundle uses ~200.
    if (form.dataset.isBundle === 'true') {
      const props = {
        _item_type:   'bundle',
        _bundle_id:   form.dataset.bundleId || '',
        _bundle_name: form.dataset.bundleName || '',
      };
      const componentsJson = readBundleComponentsJson(form.dataset.bundleId);
      if (componentsJson) props._bundle_components = componentsJson;
      payload.properties = Object.assign({}, payload.properties, props);
    }

    try {
      if (window.WillowCart && typeof window.WillowCart.add === 'function') {
        await window.WillowCart.add(payload);
      } else {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          const err = await response.json().catch(function () { return {}; });
          throw new Error(err.description || 'Failed to add item to cart.');
        }
        window.location.href = '/cart';
      }
    } catch (error) {
      console.error('[product.js]', error);
      showError(error.message || 'Something went wrong. Please try again.');
    } finally {
      if (submitBtn) submitBtn.disabled = false;
      setMode(currentMode);
      refreshAtcAvailability();
    }
  });

  // ── Error helpers ────────────────────────────────────────────────────────
  function showError(message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.removeAttribute('hidden');
  }

  function clearError() {
    if (!errorEl) return;
    errorEl.textContent = '';
    errorEl.setAttribute('hidden', '');
  }

  // ── Gallery slider (mobile) ──────────────────────────────────────────────
  // Splide se inicializa sobre el elemento #product-gallery-splide.
  // En desktop el elemento está oculto con CSS, pero Splide se monta igualmente
  // para que la transición sea inmediata al rotar el dispositivo.
  (function initGallerySlider() {
    var sliderEl = document.getElementById('product-gallery-splide');
    if (!sliderEl || typeof Splide === 'undefined') return;

    // Skincare mobile galleries pair the main slider with a native thumbnail
    // strip ([data-product-thumbs]). When present we swap pagination dots for
    // thumbnails; everything else (Supplements) keeps the dots.
    var thumbsWrap = document.querySelector('[data-product-thumbs]');
    var hasThumbs  = !!thumbsWrap;

    var gallery = new Splide(sliderEl, {
      type      : 'slide',
      rewind    : true,
      arrows    : false,
      pagination: !hasThumbs,
      gap       : 0,
      speed     : 400,
    });

    // WHY we set height on the ROOT, not the track:
    // Splide's Layout component owns .splide__track height and recalculates it
    // (to the tallest slide) after every resize via its internal ResizeObserver.
    // Any height we set on the track is reliably overwritten. The root .splide
    // element is never touched by Splide's layout engine, so setting height there
    // is safe. Combined with overflow:hidden on the root (set in CSS), this clips
    // the over-tall track to exactly the active slide's natural height.
    function updateHeight(index) {
      var slideEls = sliderEl.querySelectorAll('.splide__slide');
      if (!slideEls[index]) return;

      var img = slideEls[index].querySelector('img');
      if (!img) return;

      var naturalW = parseInt(img.getAttribute('width'),  10);
      var naturalH = parseInt(img.getAttribute('height'), 10);
      if (!naturalW || !naturalH) return;

      var w = sliderEl.offsetWidth;
      if (w <= 0) return; // slider is display:none on desktop — skip

      sliderEl.style.height = Math.round(w * naturalH / naturalW) + 'px';
    }

    // Pull the pagination element out of the slider root so it sits in
    // normal flow BELOW the (height-clipped) slider instead of overlaying
    // the image. Splide attaches click handlers to each pagination button
    // directly (not via event delegation on the parent <ul>), so relocating
    // the DOM after mount keeps navigation working. We rerun this on every
    // refresh too — Splide rebuilds the pagination element when slides
    // change, which would otherwise reinsert it back inside the slider.
    function relocatePagination() {
      var pagination = sliderEl.querySelector(':scope > .splide__pagination');
      if (!pagination) return;
      var parent = sliderEl.parentNode;
      if (!parent) return;
      parent.insertBefore(pagination, sliderEl.nextSibling);
    }

    gallery.on('mounted',   function () { updateHeight(gallery.index); relocatePagination(); });
    gallery.on('moved',     function (newIndex) { updateHeight(newIndex); });
    gallery.on('refresh',   relocatePagination);
    gallery.on('updated',   relocatePagination);

    gallery.mount();

    if (hasThumbs) {
      var track     = thumbsWrap.querySelector('.js-thumbs-track');
      var nextBtn   = thumbsWrap.querySelector('.js-thumbs-next');
      var thumbBtns = Array.prototype.slice.call(thumbsWrap.querySelectorAll('.js-thumb'));

      // Highlight the thumbnail matching the main image and keep it in view —
      // scrolling only the strip, never the page.
      var setCurrentThumb = function (index) {
        thumbBtns.forEach(function (btn, i) {
          var li = btn.parentElement;
          if (li) li.classList.toggle('is-current', i === index);
        });
        var current = thumbBtns[index];
        if (!current || !track) return;
        var left  = current.offsetLeft;
        var right = left + current.offsetWidth;
        if (left < track.scrollLeft) {
          track.scrollTo({ left: left, behavior: 'smooth' });
        } else if (right > track.scrollLeft + track.clientWidth) {
          track.scrollTo({ left: right - track.clientWidth, behavior: 'smooth' });
        }
      };

      // The next arrow advances to the next image (and so the next thumbnail).
      // It hides on the last image — space reserved via CSS so nothing shifts.
      var updateThumbArrows = function () {
        if (!nextBtn) return;
        nextBtn.classList.toggle('is-hidden', gallery.index >= gallery.length - 1);
      };

      // Tap a thumbnail → show that image in the main viewer.
      thumbBtns.forEach(function (btn, i) {
        btn.addEventListener('click', function () { gallery.go(i); });
      });

      if (nextBtn) nextBtn.addEventListener('click', function () {
        if (gallery.index < gallery.length - 1) gallery.go(gallery.index + 1);
      });

      // Keep the highlight + arrow synced whether the user taps a thumb, taps
      // the arrow, or swipes the main image.
      gallery.on('moved', function (newIndex) {
        setCurrentThumb(newIndex);
        updateThumbArrows();
      });

      setCurrentThumb(gallery.index);
      updateThumbArrows();
    }

    window.addEventListener('resize', function () { updateHeight(gallery.index); });
  }());

  // ── Accordion animation ──────────────────────────────────────────────────
  // CSS `transition: height` on .product-accordion__body.
  // We intercept <summary> clicks to animate height 0 ↔ scrollHeight
  // before the browser toggles the [open] attribute / display:none.
  (function initAccordions() {
    document.querySelectorAll('.product-accordion').forEach(function (details) {
      const summary = details.querySelector('.product-accordion__summary');
      const body    = details.querySelector('.product-accordion__body');
      if (!summary || !body) return;

      var animating = false;

      summary.addEventListener('click', function (event) {
        event.preventDefault();
        if (animating) return;
        animating = true;

        if (details.open) {
          // ── Closing ──────────────────────────────────────────────────────
          // Lock height so the transition has a defined start value,
          // then animate to 0. Remove [open] only after the transition ends
          // (otherwise the browser applies display:none immediately).
          body.style.height = body.scrollHeight + 'px';
          body.offsetHeight; // force reflow
          body.style.height = '0';

          body.addEventListener('transitionend', function () {
            details.removeAttribute('open');
            body.style.height = '';
            animating = false;
          }, { once: true });

        } else {
          // ── Opening ──────────────────────────────────────────────────────
          // Set [open] so content is in the flow, start from height 0,
          // then animate to scrollHeight. Two rAFs ensure the browser has
          // painted the open state before we start the transition.
          details.setAttribute('open', '');
          body.style.height = '0';

          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              body.style.height = body.scrollHeight + 'px';

              body.addEventListener('transitionend', function () {
                body.style.height = 'auto'; // restore auto so content reflows on resize
                animating = false;
              }, { once: true });
            });
          });
        }
      });
    });
  }());

  // ── Supplement Facts Modal ───────────────────────────────────────────────
  // Opens when any <a href="#supplement-facts"> is clicked.
  // Closes on backdrop click, close button click, or Escape key.
  (function initSuppFactsModal() {
    const modal = document.getElementById('supplement-facts-modal');
    if (!modal) return;

    const closeBtn = modal.querySelector('.js-supp-facts-close');

    function openModal() {
      modal.classList.add('is-open');
      if (closeBtn) closeBtn.focus();
    }

    function closeModal() {
      modal.classList.remove('is-open');
    }

    document.querySelectorAll('a[href="#supplement-facts"]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        openModal();
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', closeModal);

    modal.addEventListener('click', function (e) {
      if (e.target === modal) closeModal();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
    });
  }());
})();
